import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Save, Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import {
  createEvent,
  EventInput,
  fetchAdminEventById,
  updateEvent,
} from "@/services/eventService";

const eventSchema = z.object({
  titleEn: z.string().min(3, "English title is required"),
  titleFr: z.string().min(3, "French title is required"),
  slug: z.string().min(3, "Slug is required"),
  date: z.string().min(1, "Date is required"),
  shortDescriptionEn: z.string().min(10, "Add a short English description"),
  shortDescriptionFr: z.string().min(10, "Add a short French description"),
  descriptionEn: z.string().min(20, "Add a full English description"),
  descriptionFr: z.string().min(20, "Add a full French description"),
  coverImage: z.string().optional(),
  images: z.array(z.string()).default([]),
  isPublished: z.boolean().default(true),
});

type EventFormValues = z.infer<typeof eventSchema>;

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const EventEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [slugTouched, setSlugTouched] = useState(false);

  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      titleEn: "",
      titleFr: "",
      slug: "",
      date: new Date().toISOString().split("T")[0],
      shortDescriptionEn: "",
      shortDescriptionFr: "",
      descriptionEn: "",
      descriptionFr: "",
      coverImage: "",
      images: [],
      isPublished: true,
    },
  });

  const { data: existingEvent, isLoading: isLoadingEvent } = useQuery({
    queryKey: ["admin", "events", id],
    queryFn: () => fetchAdminEventById(id as string),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existingEvent) return;

    form.reset({
      titleEn: existingEvent.title.en,
      titleFr: existingEvent.title.fr,
      slug: existingEvent.slug,
      date: existingEvent.date,
      shortDescriptionEn: existingEvent.shortDescription.en,
      shortDescriptionFr: existingEvent.shortDescription.fr,
      descriptionEn: existingEvent.description.en,
      descriptionFr: existingEvent.description.fr,
      coverImage: existingEvent.coverImage || "",
      images: existingEvent.images.map((image) => image.url),
      isPublished: Boolean(existingEvent.isPublished),
    });
  }, [existingEvent, form]);

  const watchedTitleEn = form.watch("titleEn");
  const watchedImages = form.watch("images");

  useEffect(() => {
    if (slugTouched) return;
    if (!watchedTitleEn) return;
    form.setValue("slug", toSlug(watchedTitleEn));
  }, [watchedTitleEn, slugTouched, form]);

  const mutation = useMutation({
    mutationFn: async (values: EventFormValues) => {
      const payload: EventInput = {
        ...values,
        coverImage: values.coverImage || values.images[0] || "",
      };

      return isEdit && id ? updateEvent(id, payload) : createEvent(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      queryClient.invalidateQueries({ queryKey: ["events", "published"] });
      toast({
        title: isEdit ? "Event updated" : "Event created",
        description: isEdit
          ? "The event was updated successfully."
          : "The new event is now ready.",
      });
      navigate("/admin/events");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save event.",
        variant: "destructive",
      });
    },
  });

  const pageTitle = useMemo(
    () => (isEdit ? "Edit Event | Admin Dashboard" : "Add Event | Admin Dashboard"),
    [isEdit],
  );

  const onUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    const uploadedUrls: string[] = [];

    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const ext = file.name.split(".").pop();
        const fileName = `event-${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
        const filePath = `events/${fileName}`;

        const { error } = await supabase.storage.from("events").upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

        if (error) throw error;

        const { data } = supabase.storage.from("events").getPublicUrl(filePath);
        uploadedUrls.push(data.publicUrl);
        setUploadProgress(Math.round(((index + 1) / files.length) * 100));
      }

      const current = form.getValues("images") || [];
      form.setValue("images", [...current, ...uploadedUrls]);
      if (!form.getValues("coverImage") && uploadedUrls[0]) {
        form.setValue("coverImage", uploadedUrls[0]);
      }
    } catch (error: any) {
      toast({
        title: "Upload error",
        description: error.message || "Failed to upload event images.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (isEdit && isLoadingEvent) {
    return <div className="py-10 text-center text-muted-foreground">Loading event...</div>;
  }

  return (
    <div>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <div className="flex items-center mb-6">
        <Button variant="ghost" className="mr-2" onClick={() => navigate("/admin/events")}>
          <ArrowLeft size={16} />
        </Button>
        <h1 className="text-2xl font-sans font-semibold">
          {isEdit ? "Edit Event" : "Add New Event"}
        </h1>
      </div>

      <div className="bg-white rounded-md shadow-sm p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit((values) => mutation.mutate(values))} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="titleEn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title (EN)</FormLabel>
                    <FormControl>
                      <Input placeholder="MK Design Week 2026" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="titleFr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title (FR)</FormLabel>
                    <FormControl>
                      <Input placeholder="MK Design Week 2026" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slug</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="mk-design-week-2026"
                        {...field}
                        onChange={(event) => {
                          setSlugTouched(true);
                          field.onChange(toSlug(event.target.value));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Event Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="shortDescriptionEn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Description (EN)</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="shortDescriptionFr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Description (FR)</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="descriptionEn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Description (EN)</FormLabel>
                    <FormControl>
                      <Textarea rows={6} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="descriptionFr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Description (FR)</FormLabel>
                    <FormControl>
                      <Textarea rows={6} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <div>
                <FormLabel>Gallery Images</FormLabel>
                <div className="border border-input rounded-md p-4 space-y-4">
                  <label
                    htmlFor="event-upload"
                    className="flex items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-md cursor-pointer hover:bg-muted/40"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={22} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Upload event images</span>
                    </div>
                    <input
                      id="event-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={onUpload}
                      disabled={isUploading}
                    />
                  </label>

                  {isUploading && (
                    <div>
                      <div className="w-full bg-muted h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-furniture-teal h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Uploading... {uploadProgress}%</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {watchedImages.map((url, index) => (
                      <div key={`${url}-${index}`} className="relative group">
                        <img src={url} alt={`event-${index}`} className="w-full h-24 object-cover rounded-md" />
                        <button
                          type="button"
                          className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => {
                            const copy = [...watchedImages];
                            copy.splice(index, 1);
                            form.setValue("images", copy);
                            if (form.getValues("coverImage") === url) {
                              form.setValue("coverImage", copy[0] || "");
                            }
                          }}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <FormField
                control={form.control}
                name="coverImage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cover Image URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="button"
                variant="outline"
                onClick={() => form.setValue("coverImage", watchedImages[0] || "")}
              >
                Use first gallery image as cover
              </Button>
            </div>

            <FormField
              control={form.control}
              name="isPublished"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel>Published (visible on website)</FormLabel>
                </FormItem>
              )}
            />

            <div className="pt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/admin/events")}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending || isUploading}>
                {mutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Event
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default EventEditor;
